/**
 * useDeepResearch — manages deep research streaming state.
 *
 * Activated when URL contains `mode=research&keywords=...`.
 * Streams through the deepResearchStream API, applying filters
 * to the URL as the AI analyses the query.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { parse, stringify } from '../../../util/urlHelpers';
import { deepResearchStream } from '../../../util/api';

/**
 * @param {Object} deps
 * @param {Object} deps.location — react-router location
 * @param {Object} deps.history — react-router history
 */
export default function useDeepResearch({ location, history }) {
  const queryParams = useMemo(() => parse(location.search), [location.search]);
  const isResearchMode = queryParams.mode === 'research' && !!queryParams.keywords;

  const [isResearching, setIsResearching] = useState(false);
  const [researchSteps, setResearchSteps] = useState([]);
  const [researchAnnotations, setResearchAnnotations] = useState([]);
  const [researchSummary, setResearchSummary] = useState(null);
  const [researchConfidence, setResearchConfidence] = useState(null);
  const [researchExplanation, setResearchExplanation] = useState(null);
  const [researchError, setResearchError] = useState(null);
  const [researchPhase, setResearchPhase] = useState('starting');
  const [researchQuery, setResearchQuery] = useState('');

  const abortRef = useRef(null);
  const startedQueryRef = useRef(null);

  // Translate AI filter steps into URL search params
  const applyFilterToUrl = useCallback(
    (filter, value) => {
      const current = parse(location.search);
      const updates = { ...current };

      switch (filter) {
        case 'category':
          updates.pub_categoryLevel1 = value;
          break;
        case 'subcategory':
          updates.pub_categoryLevel2 = value;
          break;
        case 'price': {
          const [min, max] = value.split(',').map(Number);
          if (!isNaN(min)) updates.price = `${min * 100},${max * 100}`;
          break;
        }
        case 'condition':
          updates.pub_condition_id = value;
          break;
        case 'sort':
          updates.sort = value;
          break;
        case 'keywords':
          updates.keywords = value;
          break;
        default:
          break;
      }

      updates.mode = 'research';
      const newSearch = stringify(updates);
      history.replace(`${location.pathname}?${newSearch}`);
    },
    [location.search, location.pathname, history]
  );

  // Start research stream when mode=research is detected
  useEffect(() => {
    if (!isResearchMode) {
      if (startedQueryRef.current) {
        startedQueryRef.current = null;
        setIsResearching(false);
        setResearchSteps([]);
        setResearchAnnotations([]);
        setResearchSummary(null);
        setResearchConfidence(null);
        setResearchExplanation(null);
        setResearchError(null);
        setResearchPhase('starting');
        setResearchQuery('');
        if (abortRef.current) {
          abortRef.current();
          abortRef.current = null;
        }
      }
      return;
    }

    const keywords = queryParams.keywords;
    if (startedQueryRef.current === keywords) return;
    startedQueryRef.current = keywords;

    setIsResearching(true);
    setResearchSteps([]);
    setResearchAnnotations([]);
    setResearchSummary(null);
    setResearchError(null);
    setResearchPhase('starting');
    setResearchQuery(keywords);

    let filterQueue = [];
    let isProcessingQueue = false;

    const processFilterQueue = () => {
      if (isProcessingQueue || filterQueue.length === 0) return;
      isProcessingQueue = true;
      const step = filterQueue.shift();
      applyFilterToUrl(step.filter, step.value);
      setTimeout(() => {
        isProcessingQueue = false;
        processFilterQueue();
      }, 800);
    };

    const abort = deepResearchStream(keywords, {
      callbacks: {
        onResearchStep: step => {
          setResearchPhase('filtering');
          setResearchSteps(prev => [...prev, step]);
          filterQueue.push(step);
          processFilterQueue();
        },
        onToolStart: toolName => {
          if (toolName === 'db_search' || toolName === 'price_check') {
            setResearchPhase('analysing');
          } else if (toolName === 'annotate_listings') {
            setResearchPhase('annotating');
          }
        },
        onAnnotations: annotations => {
          setResearchAnnotations(annotations);
        },
        onDone: event => {
          setIsResearching(false);
          setResearchPhase('complete');
          setResearchSummary(event.message || 'Research complete.');
          if (event?.confidence != null) setResearchConfidence(event.confidence);
          if (event?.explanation) setResearchExplanation(event.explanation);
        },
        onError: error => {
          setIsResearching(false);
          setResearchPhase('error');
          setResearchError(error || 'Something went wrong');
        },
      },
    });

    abortRef.current = abort;

    return () => {
      if (abortRef.current) {
        abortRef.current();
        abortRef.current = null;
      }
    };
  }, [isResearchMode, queryParams.keywords, applyFilterToUrl]);

  // Dismiss: remove mode=research from URL
  const handleDismissResearch = useCallback(() => {
    const current = parse(location.search);
    delete current.mode;
    const newSearch = stringify(current);
    history.replace(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
  }, [location.search, location.pathname, history]);

  return {
    isResearching,
    researchSteps,
    researchAnnotations,
    researchSummary,
    researchConfidence,
    researchExplanation,
    researchError,
    researchPhase,
    researchQuery,
    handleDismissResearch,
  };
}
